import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

// Loading stage definitions
interface LoadingStage {
  id: string;
  name: string;
  weight: number; // Contribution to overall progress (0-100)
  dependencies: string[];
  completed: boolean;
}

interface GlobalLoadingState {
  isAppLoading: boolean; // Legacy: Controls full app loading (preserved for compatibility)
  isCriticalLoading: boolean; // New: Controls UI revelation based on critical stages only
  backgroundTasksActive: boolean; // New: Tracks if background tasks are running
  stages: LoadingStage[];
  progress: number; // 0-100
  currentStage: string;
  completedStages: string[];
  errors: string[];
  loadingSource: 'login' | 'refresh' | 'account-switch' | '';
  currentRoute: string; // Track current route for loading decisions
  backgroundQueue: string[]; // Stages to load in background
}

type LoadingAction = 
  | { type: 'COMPLETE_STAGE'; stageId: string }
  | { type: 'SET_CURRENT_STAGE'; stageId: string }
  | { type: 'ADD_ERROR'; error: string }
  | { type: 'FINISH_LOADING' }
  | { type: 'FINISH_CRITICAL_LOADING' }
  | { type: 'SET_BACKGROUND_TASKS_ACTIVE'; active: boolean }
  | { type: 'RESET_LOADING' }
  | { type: 'START_LOGIN_FLOW' }
  | { type: 'START_DATA_LOADING'; source: 'login' | 'refresh' | 'account-switch' }
  | { type: 'RESET_FOR_ACCOUNT_SWITCH' }
  | { type: 'SET_CURRENT_ROUTE'; route: string }
  | { type: 'ADD_TO_BACKGROUND_QUEUE'; stageId: string }
  | { type: 'REMOVE_FROM_BACKGROUND_QUEUE'; stageId: string };

interface GlobalLoadingContextType {
  state: GlobalLoadingState;
  completeStage: (stageId: string) => void;
  setCurrentStage: (stageId: string) => void;
  addError: (error: string) => void;
  finishLoading: () => void;
  resetLoading: () => void;
  startLoginFlow: () => void;
  startDataLoading: (source: 'login' | 'refresh' | 'account-switch') => void;
  resetForAccountSwitch: () => void;
  setCurrentRoute: (route: string) => void;
  shouldLoadStage: (stageId: string) => boolean;
  isStageInBackgroundQueue: (stageId: string) => boolean;
}

const loadingStages: LoadingStage[] = [
  {
    id: 'authentication',
    name: 'Authenticating user...',
    weight: 10,
    dependencies: [],
    completed: false
  },
  {
    id: 'coreApp',
    name: 'Loading application...',
    weight: 10,
    dependencies: ['authentication'],
    completed: false
  },
  {
    id: 'meetings',
    name: 'Loading meetings...',
    weight: 25,
    dependencies: ['authentication'],
    completed: false
  },
  {
    id: 'high-priority-conversations',
    name: 'Loading recent conversations...',
    weight: 20,
    dependencies: ['meetings'],
    completed: false
  },
  {
    id: 'remaining-conversations',
    name: 'Loading all conversations...',
    weight: 15,
    dependencies: ['high-priority-conversations'],
    completed: false
  },
  {
    id: 'features',
    name: 'Preparing features...',
    weight: 5,
    dependencies: ['authentication'],
    completed: false
  }
];

// Determine critical stages and background stages based on route
const getCriticalAndBackgroundStages = (route: string, isAuthenticated: boolean = false): { critical: string[]; background: string[] } => {
  const baseStages = ['authentication', 'coreApp', 'features'];
  
  if (route === '/' || route === '/calendar') {
    // CRM pages: SMART PRIORITIZATION - only 3 ultra-high-priority conversations are critical
    // This ensures ultra-fast app reveal while maintaining comprehensive data loading
    return {
      critical: [...baseStages, 'meetings', 'ultra-high-priority-conversations'],
      background: ['high-priority-conversations', 'remaining-conversations']
    };
  } else if (route === '/login' && isAuthenticated) {
    // Login page with authenticated user: include main page critical stages
    // This prevents the loading screen from unmounting/remounting during navigation
    return {
      critical: [...baseStages, 'meetings', 'ultra-high-priority-conversations'],
      background: ['high-priority-conversations', 'remaining-conversations']
    };
  } else {
    // Other pages (like login without auth): only base stages are critical
    return {
      critical: baseStages,
      background: ['meetings', 'ultra-high-priority-conversations', 'high-priority-conversations', 'remaining-conversations']
    };
  }
};

const initialState: GlobalLoadingState = {
  isAppLoading: true, // Legacy compatibility
  isCriticalLoading: true, // New: Controls UI revelation
  backgroundTasksActive: false, // New: Tracks background tasks
  stages: loadingStages,
  progress: 0,
  currentStage: 'authentication',
  completedStages: [],
  errors: [],
  loadingSource: 'refresh', // Default to refresh for initial app load
  currentRoute: '/',
  backgroundQueue: []
};

const calculateProgress = (completedStages: string[], stages: LoadingStage[]): number => {
  const totalWeight = stages.reduce((sum, stage) => sum + stage.weight, 0);
  const completedWeight = stages
    .filter(stage => completedStages.includes(stage.id))
    .reduce((sum, stage) => sum + stage.weight, 0);
  
  return Math.round((completedWeight / totalWeight) * 100);
};

const loadingReducer = (state: GlobalLoadingState, action: LoadingAction): GlobalLoadingState => {
  switch (action.type) {
    case 'COMPLETE_STAGE': {
      const newCompletedStages = [...state.completedStages, action.stageId];
      const newStages = state.stages.map(stage => 
        stage.id === action.stageId ? { ...stage, completed: true } : stage
      );
      const newProgress = calculateProgress(newCompletedStages, newStages);
      
      return {
        ...state,
        completedStages: newCompletedStages,
        stages: newStages,
        progress: newProgress
      };
    }
    
    case 'SET_CURRENT_STAGE':
      return {
        ...state,
        currentStage: action.stageId
      };
    
    case 'ADD_ERROR':
      return {
        ...state,
        errors: [...state.errors, action.error]
      };
    
    case 'FINISH_LOADING':
      return {
        ...state,
        isAppLoading: false,
        isCriticalLoading: false,
        progress: 100
      };
    
    case 'FINISH_CRITICAL_LOADING':
      return {
        ...state,
        isCriticalLoading: false
      };
    
    case 'SET_BACKGROUND_TASKS_ACTIVE':
      return {
        ...state,
        backgroundTasksActive: action.active
      };
    
    case 'RESET_LOADING':
      return {
        ...initialState,
        currentRoute: state.currentRoute, // Preserve current route
        stages: loadingStages.map(stage => ({ ...stage, completed: false }))
      };
    
    case 'START_LOGIN_FLOW':
      return {
        ...initialState,
        isAppLoading: true,
        isCriticalLoading: true,
        backgroundTasksActive: false,
        currentStage: 'authentication',
        loadingSource: 'login',
        currentRoute: state.currentRoute, // Preserve current route
        stages: loadingStages.map(stage => ({ ...stage, completed: false }))
      };
    
    case 'START_DATA_LOADING':
      return {
        ...initialState,
        isAppLoading: true,
        isCriticalLoading: true,
        backgroundTasksActive: false,
        currentStage: action.source === 'account-switch' ? 'meetings' : 'authentication',
        loadingSource: action.source,
        currentRoute: state.currentRoute, // Preserve current route
        stages: loadingStages.map(stage => ({ ...stage, completed: false }))
      };
    
    case 'RESET_FOR_ACCOUNT_SWITCH':
      return {
        ...state,
        isAppLoading: true,
        isCriticalLoading: true,
        backgroundTasksActive: false,
        currentStage: 'meetings',
        loadingSource: 'account-switch',
        completedStages: ['authentication', 'coreApp', 'features'], // Skip non-data stages for account switch
        backgroundQueue: [], // Clear background queue for new account
        stages: state.stages.map(stage => ({
          ...stage,
          completed: ['authentication', 'coreApp', 'features'].includes(stage.id)
        })),
        progress: calculateProgress(['authentication', 'coreApp', 'features'], loadingStages)
      };
    
    case 'SET_CURRENT_ROUTE': {
      // When route changes, check if we need to restart critical loading for new critical stages
      const newRoute = action.route;
      const isAuthenticated = state.completedStages.includes('authentication');
      const { critical: newCriticalStages } = getCriticalAndBackgroundStages(newRoute, isAuthenticated);
      const hasUncompletedCriticalStages = newCriticalStages.some(
        stageId => !state.completedStages.includes(stageId)
      );
      
      // If we're navigating to a route with uncompleted critical stages and we're not already
      // in critical loading, restart critical loading ONLY during initial load (never on navigation)
      const isInitialLoad = (state.loadingSource === 'login' || state.completedStages.length === 0) &&
                           state.isCriticalLoading; // Only during actual initial load, not navigation
      const shouldRestartCritical = hasUncompletedCriticalStages &&
                                   !state.isCriticalLoading &&
                                   state.isAppLoading &&
                                   isInitialLoad; // NEVER restart after initial app reveal
      
      if (shouldRestartCritical) {
        // console.log(`Route change to ${newRoute} requires critical stages that aren't complete - restarting critical loading`);
      }
      
      return {
        ...state,
        currentRoute: newRoute,
        // Restart critical loading if needed for the new route
        isCriticalLoading: shouldRestartCritical ? true : state.isCriticalLoading
      };
    }
    
    case 'ADD_TO_BACKGROUND_QUEUE':
      return {
        ...state,
        backgroundQueue: [...new Set([...state.backgroundQueue, action.stageId])]
      };
    
    case 'REMOVE_FROM_BACKGROUND_QUEUE':
      return {
        ...state,
        backgroundQueue: state.backgroundQueue.filter(id => id !== action.stageId)
      };
    
    default:
      return state;
  }
};

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

export const useGlobalLoading = (): GlobalLoadingContextType => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
};

interface GlobalLoadingProviderProps {
  children: ReactNode;
}

export const GlobalLoadingProvider: React.FC<GlobalLoadingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(loadingReducer, initialState);

  // Set initial route on mount (only once)
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== state.currentRoute && state.currentRoute === '/') {
      dispatch({ type: 'SET_CURRENT_ROUTE', route: currentPath });
    }
  }, []); // Empty dependency array ensures this only runs once

  // Authentication completion will be handled by AuthContext via completeStage('authentication')

  // Auto-complete core app stage after authentication
  useEffect(() => {
    if (state.completedStages.includes('authentication') && !state.completedStages.includes('coreApp')) {
      // console.log(`Core app initialization completed`);
      dispatch({ type: 'SET_CURRENT_STAGE', stageId: 'coreApp' });
      // Simulate core app loading time
      setTimeout(() => {
        dispatch({ type: 'COMPLETE_STAGE', stageId: 'coreApp' });
      }, 500);
    }
  }, [state.completedStages]);

  // Auto-complete features stage after core app
  useEffect(() => {
    if (state.completedStages.includes('coreApp') && !state.completedStages.includes('features')) {
      // console.log(`Features initialization completed`);
      dispatch({ type: 'SET_CURRENT_STAGE', stageId: 'features' });
      // Simulate features loading time
      setTimeout(() => {
        dispatch({ type: 'COMPLETE_STAGE', stageId: 'features' });
      }, 300);
    }
  }, [state.completedStages]);


  // Handle critical loading completion - finish critical loading when page-specific critical stages are complete
  useEffect(() => {
    if (!state.isCriticalLoading) {
      return; // Only finish critical loading when app is currently in critical loading state
    }

    // Only proceed if authentication is complete (indicates user is authenticated)
    if (!state.completedStages.includes('authentication')) {
      return; // Wait for authentication to complete first
    }

    // Safety check: ensure we have a loading source (prevents undefined behavior)
    if (!state.loadingSource) {
      console.warn('🚨 Loading source is undefined - this may indicate initialization issue');
      return;
    }

    // Determine critical stages based on current route
    const isAuthenticated = state.completedStages.includes('authentication');
    const { critical: criticalStages, background: backgroundStages } = getCriticalAndBackgroundStages(state.currentRoute, isAuthenticated);
    
    const allCriticalCompleted = criticalStages.every(stageId => 
      state.completedStages.includes(stageId)
    );
    
    // Add background stages to queue when critical stages complete
    if (allCriticalCompleted && state.backgroundQueue.length === 0 && backgroundStages.length > 0) {
      // Adding background stages to queue: ${backgroundStages.join(', ')}
      backgroundStages.forEach(stageId => {
        dispatch({ type: 'ADD_TO_BACKGROUND_QUEUE', stageId });
      });
      // Mark background tasks as active
      dispatch({ type: 'SET_BACKGROUND_TASKS_ACTIVE', active: true });
    }
    
    // Finish critical loading when all critical stages are completed (UI can now show)
    if (allCriticalCompleted && state.isCriticalLoading) {
      // Keep this log - it's a major lifecycle event
      console.log(`Critical loading completed for ${state.currentRoute} - revealing application - ${new Date().toISOString()}`);
      dispatch({ type: 'FINISH_CRITICAL_LOADING' });
    }
  }, [state.completedStages, state.isCriticalLoading, state.currentRoute, state.backgroundQueue]);

  // Handle full app loading completion - finish when all stages (including background) are complete
  useEffect(() => {
    if (!state.isAppLoading) {
      return; // Only finish when app is currently in loading state
    }

    // Check if all stages are completed (both critical and background)
    const allStagesCompleted = state.stages.every(stage => state.completedStages.includes(stage.id));

    if (allStagesCompleted) {
      // Keep this log - it's a major lifecycle event
      console.log(`All loading stages completed - finishing full app loading - ${new Date().toISOString()}`);
      dispatch({ type: 'FINISH_LOADING' });
      dispatch({ type: 'SET_BACKGROUND_TASKS_ACTIVE', active: false });
    }
  }, [state.completedStages, state.isAppLoading]);

  // Monitor background queue to update background tasks status
  useEffect(() => {
    const hasBackgroundTasks = state.backgroundQueue.length > 0;
    if (state.backgroundTasksActive !== hasBackgroundTasks) {
      dispatch({ type: 'SET_BACKGROUND_TASKS_ACTIVE', active: hasBackgroundTasks });
    }
  }, [state.backgroundQueue, state.backgroundTasksActive]);

  const completeStage = (stageId: string) => {
    // Prevent duplicate stage completions
    if (state.completedStages.includes(stageId)) {
      // console.log(`Stage ${stageId} already completed, skipping - ${new Date().toISOString()}`);
      return;
    }

    // Verbose debug log - commented for production (keep for major events)
    console.log(`Completed loading stage: ${stageId} - ${new Date().toISOString()}`);
    dispatch({ type: 'COMPLETE_STAGE', stageId });
  };

  const setCurrentStage = (stageId: string) => {
    const stage = state.stages.find(s => s.id === stageId);
    if (stage) {
      // console.log(`Current loading stage: ${stage.name} - ${new Date().toISOString()}`);
      dispatch({ type: 'SET_CURRENT_STAGE', stageId });
    }
  };

  const addError = (error: string) => {
    console.error(`❌ Loading error: ${error}`);
    dispatch({ type: 'ADD_ERROR', error });
  };

  const finishLoading = () => {
    dispatch({ type: 'FINISH_LOADING' });
  };

  const resetLoading = () => {
    dispatch({ type: 'RESET_LOADING' });
  };

  const startLoginFlow = () => {
    // console.log(`🔐 Starting login flow - initializing unified loading - ${new Date().toISOString()}`);
    dispatch({ type: 'START_LOGIN_FLOW' });
  };

  const startDataLoading = (source: 'login' | 'refresh' | 'account-switch') => {
    // console.log(`Starting data loading - source: ${source} - ${new Date().toISOString()}`);
    dispatch({ type: 'START_DATA_LOADING', source });
  };

  const resetForAccountSwitch = () => {
    // console.log(`Resetting for account switch - ${new Date().toISOString()}`);
    dispatch({ type: 'RESET_FOR_ACCOUNT_SWITCH' });
  };

  const setCurrentRoute = (route: string) => {
    // console.log(`Setting current route: ${route} - ${new Date().toISOString()}`);
    dispatch({ type: 'SET_CURRENT_ROUTE', route });
  };

  const shouldLoadStage = (stageId: string): boolean => {
    const isAuthenticated = state.completedStages.includes('authentication');
    const { critical } = getCriticalAndBackgroundStages(state.currentRoute, isAuthenticated);
    const isCritical = critical.includes(stageId);
    const isInBackgroundQueue = state.backgroundQueue.includes(stageId);
    const isCompleted = state.completedStages.includes(stageId);
    
    // Debug logging for meetings loading issue (development only)
    if (stageId === 'meetings' && import.meta.env.DEV) {
      // console.log(`DEBUG shouldLoadStage('meetings'):`, {
      //   currentRoute: state.currentRoute,
      //   critical,
      //   isCritical,
      //   isInBackgroundQueue,
      //   isCompleted,
      //   result: (isCritical || isInBackgroundQueue) && !isCompleted
      // });
    }
    
    // Load if: it's critical for current route OR it's in background queue AND not already completed
    return (isCritical || isInBackgroundQueue) && !isCompleted;
  };

  const isStageInBackgroundQueue = (stageId: string): boolean => {
    return state.backgroundQueue.includes(stageId);
  };

  return (
    <GlobalLoadingContext.Provider value={{
      state,
      completeStage,
      setCurrentStage,
      addError,
      finishLoading,
      resetLoading,
      startLoginFlow,
      startDataLoading,
      resetForAccountSwitch,
      setCurrentRoute,
      shouldLoadStage,
      isStageInBackgroundQueue
    }}>
      {children}
    </GlobalLoadingContext.Provider>
  );
};

export default GlobalLoadingProvider;